/**
 * The project header's progress stepper: Placed → Paid → POs → shipping → Delivered.
 *
 * The Paid step counts only orders whose customer payment we actually track (USA-1432):
 * Shopify storefront orders (Shopify's payment states) and orders billed through our own
 * invoices (Quote Builder, including payments recorded by hand and prepaid quotes). Dropship
 * orders billed some other way carry placement states such as `approved` or `upload_confirmed`
 * that never become paid, so they no longer hold the project at "Awaiting payment".
 */

export interface ProgressOrder {
  financial_status?: string | null
  status?: string | null
  shopify_order_id?: number | null
  purchase_orders?: {
    status?: string | null
    shipments?: { status?: string | null; estimated_delivery_date?: string | null }[]
  }[]
}

export interface ProgressStep {
  key: string
  label: string
  status: 'complete' | 'partial' | 'pending'
  done: number
  total: number
  /** Every order has at least a deposit paid: the step shows part-paid and work moves past it. */
  depositPaid?: boolean
  /** No order's payment is tracked by us: the step reads complete and is never the current one. */
  untracked?: boolean
}

const PO_SENT_STATUSES = new Set([
  'email_sent', 'online_order_placed', 'asked_for_spec', 'po_cut', 'po_processing',
  'pull_from_warehouse', 'acknowledged', 'confirmed',
  'need_to_schedule', 'freight_booked', 'in_transit_to_site', 'partially_in_transit',
  'at_terminal_awaiting', 'partially_delivered', 'delivered_to_site',
  'shipped', 'received',
])
const PO_CONFIRMED_STATUSES = new Set([
  'po_cut', 'po_processing', 'pull_from_warehouse', 'acknowledged', 'confirmed',
  'need_to_schedule', 'freight_booked', 'in_transit_to_site', 'partially_in_transit',
  'at_terminal_awaiting', 'partially_delivered', 'delivered_to_site',
  'shipped', 'received',
])
const SHIP_BOOKED_STATUSES = new Set(['booked', 'shipped', 'in_transit', 'delivered'])
const SHIP_SHIPPED_STATUSES = new Set(['shipped', 'in_transit', 'delivered'])

/** Paid in full: Shopify's PAID (a partial refund was paid first) and our ledger's `paid`. */
const PAID_STATES = new Set(['PAID', 'PARTIALLY_REFUNDED'])
/** Nothing left to collect: the money went back or the authorization lapsed. */
const NOTHING_TO_COLLECT = new Set(['REFUNDED', 'VOIDED'])
/** Order states written by our ledger once the customer pays (`sync_order_payment_status`). */
const LEDGER_STATES = new Set(['PAID', 'PARTIALLY_PAID'])

/**
 * Whether the customer's payment for this order is something we track, given whether the
 * project bills through our invoices (it has a quote total, an invoice, a recorded payment or a
 * prepaid quote).
 */
export function orderPaymentTracked(order: ProgressOrder, billedThroughInvoices: boolean): boolean {
  const state = (order.financial_status ?? '').toUpperCase()
  if (order.shopify_order_id != null) return !NOTHING_TO_COLLECT.has(state)
  return LEDGER_STATES.has(state) || billedThroughInvoices
}

function stepStatus(done: number, total: number): 'complete' | 'partial' | 'pending' {
  if (total === 0) return 'pending'
  if (done >= total) return 'complete'
  if (done > 0) return 'partial'
  return 'pending'
}

function paidStep(orders: ProgressOrder[], billedThroughInvoices: boolean): ProgressStep {
  const live = orders.filter((o) => o.status !== 'cancelled' && o.status !== 'refunded')
  const tracked = live.filter((o) => orderPaymentTracked(o, billedThroughInvoices))
  if (live.length > 0 && tracked.length === 0) {
    return { key: 'paid', label: 'Paid', status: 'complete', done: 0, total: 0, untracked: true }
  }
  const total = tracked.length
  const states = tracked.map((o) => (o.financial_status ?? '').toUpperCase())
  const done = states.filter((state) => PAID_STATES.has(state)).length
  const depositPaid =
    done < total && total > 0 && states.every((state) => PAID_STATES.has(state) || state === 'PARTIALLY_PAID')
  return {
    key: 'paid',
    label: 'Paid',
    status: depositPaid ? 'partial' : stepStatus(done, total),
    done,
    total,
    depositPaid,
  }
}

function countStep(key: string, label: string, done: number, total: number): ProgressStep {
  return { key, label, done, total, status: stepStatus(done, total) }
}

export function computeProjectProgress(
  orders: ProgressOrder[],
  { billedThroughInvoices = false }: { billedThroughInvoices?: boolean } = {},
): { steps: ProgressStep[]; currentIndex: number } {
  const activePOs = orders.flatMap((o) => o.purchase_orders ?? [])
  const allShipments = activePOs.flatMap((po) => po.shipments ?? [])

  const steps: ProgressStep[] = [
    countStep('placed', 'Placed', orders.length > 0 ? 1 : 0, 1),
    paidStep(orders, billedThroughInvoices),
    countStep('po_created', 'POs Created', activePOs.length > 0 ? 1 : 0, 1),
    countStep('po_sent', 'POs Sent', activePOs.filter((po) => PO_SENT_STATUSES.has(po.status ?? '')).length, activePOs.length),
    countStep(
      'confirmed',
      'Confirmed',
      activePOs.filter((po) => PO_CONFIRMED_STATUSES.has(po.status ?? '')).length,
      activePOs.length,
    ),
    countStep('eta_set', 'ETA Set', allShipments.filter((s) => s.estimated_delivery_date).length, allShipments.length),
    countStep(
      'booked',
      'Booked',
      allShipments.filter((s) => SHIP_BOOKED_STATUSES.has(s.status ?? '')).length,
      allShipments.length,
    ),
    countStep(
      'shipped',
      'Shipped',
      allShipments.filter((s) => SHIP_SHIPPED_STATUSES.has(s.status ?? '')).length,
      allShipments.length,
    ),
    countStep('delivered', 'Delivered', allShipments.filter((s) => s.status === 'delivered').length, allShipments.length),
  ]

  let currentIndex = steps.findIndex((s) => s.status !== 'complete' && !s.depositPaid)
  if (currentIndex === -1) currentIndex = steps.length - 1

  return { steps, currentIndex }
}

const STEP_NEXT_ACTION: Record<string, string> = {
  placed: 'Awaiting order',
  paid: 'Awaiting payment',
  po_created: 'Creating purchase orders',
  po_sent: 'Sending purchase orders',
  confirmed: 'Awaiting confirmation',
  eta_set: 'Awaiting shipping ETA',
  booked: 'Booking freight',
  shipped: 'Awaiting shipment',
  delivered: 'Awaiting delivery',
}

export function progressSummary(steps: ProgressStep[], currentIndex: number, poReview = false): string {
  const allComplete = steps.every((s) => s.status === 'complete')
  if (allComplete) return 'Complete'
  const current = steps[currentIndex]
  if (!current) return ''
  // A project started from a quote holds its POs until Sales places them (USA-1333).
  if (poReview && current.key === 'po_sent') return 'Waiting for PO review'
  const action = STEP_NEXT_ACTION[current.key] ?? current.label
  if (current.status === 'partial' && current.total > 1) {
    return `${action} (${current.done}/${current.total})`
  }
  return action
}

export function stepTooltip(step: ProgressStep): string {
  if (step.untracked) return `${step.label} — Not billed through our invoices`
  if (step.total === 0) return step.label
  if (step.depositPaid) return `${step.label} — Deposit received`
  if (step.total === 1) return `${step.label} — ${step.status === 'complete' ? 'Done' : 'Pending'}`
  return `${step.label} — ${step.done} of ${step.total}`
}

/** Whether the project's customer is billed through our invoices (Quote Builder ledger). */
export function billsThroughInvoices(
  payments:
    | {
        quote_total: string | number
        invoices: { status: string }[]
        entries: unknown[]
        prepaid?: unknown[] | null
      }
    | undefined,
): boolean {
  if (!payments) return false
  return (
    Number(payments.quote_total) > 0 ||
    payments.invoices.some((invoice) => invoice.status !== 'void') ||
    payments.entries.length > 0 ||
    (payments.prepaid?.length ?? 0) > 0
  )
}
